/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { Typography } from '../typography';

interface Step {
  /** Content displayed as the step label */
  content: React.ReactNode;
  /** Explicit status for the step. When set to `error`, the step is rendered in an error state */
  status?: 'default' | 'error';
}

interface BaseWizardProps {
  /** Layout orientation of the wizard */
  orientation?: 'horizontal' | 'vertical';
  /** Visual size of the wizard */
  size?: 'small' | 'large';
  /** Additional style */
  style?: React.CSSProperties;
  /** Additional class name */
  className?: string;
  /** Zero-based index of the active step */
  activeStepIndex: number;
  /** Steps to display in the wizard */
  steps: Step[];
}

type HorizontalWizardProps = BaseWizardProps & {
  orientation: 'horizontal';
  /** Alignment of labels/content, is set to `middle` for horizontal orientation and can be set to `top` or `middle` for vertical orientation and is not applicable when `orientation` is undefined */
  alignment?: 'middle';
};

type VerticalWizardProps = BaseWizardProps & {
  orientation: 'vertical';
  /** Alignment of labels/content, is set to `middle` for horizontal orientation and can be set to `top` or `middle` for vertical orientation and is not applicable when `orientation` is undefined */
  alignment?: 'top' | 'middle';
};

type NonOrientationWizardProps = BaseWizardProps & {
  orientation: undefined;
  /** Alignment of labels/content, is set to `middle` for horizontal orientation and can be set to `top` or `middle` for vertical orientation and is not applicable when `orientation` is undefined */
  alignment?: never;
};

/**
 * @remarks
 * Where type `Step` is defined as: `{ content: string | React.JSX.Element; status?: 'default' | 'error' }`. To highlight a step as error, set the `status` to `error`.
 */
type WizardProps =
  | HorizontalWizardProps
  | VerticalWizardProps
  | NonOrientationWizardProps;

export const Wizard = <T extends React.ElementType = 'div'>({
  as,
  className,
  style,
  size = 'large',
  steps,
  activeStepIndex,
  orientation,
  htmlAttributes,
  alignment = orientation === 'vertical' ? 'top' : 'middle',
  ref,
}: PolymorphicCommonProps<T, WizardProps>) => {
  useEffect(() => {
    if (activeStepIndex > steps.length - 1 || activeStepIndex < 0) {
      console.warn(
        `[🪡 Needle]: The activeStepIndex in Wizard is out of bounds. The activeStepIndex is ${activeStepIndex} and the number of steps is ${steps.length}.`,
      );
    }
  }, [activeStepIndex, steps.length]);

  switch (size) {
    case 'small':
      return (
        <WizardSmall
          as={as}
          orientation={orientation}
          style={style}
          className={className}
          steps={steps}
          activeStepIndex={activeStepIndex}
          ref={ref}
          htmlAttributes={htmlAttributes}
          alignment={alignment}
        />
      );
    case 'large':
      return (
        <WizardLarge
          as={as}
          orientation={orientation}
          style={style}
          className={className}
          steps={steps}
          activeStepIndex={activeStepIndex}
          ref={ref}
          htmlAttributes={htmlAttributes}
          alignment={alignment}
        />
      );
  }
};

const getStatus = (activeStepIndex: number, stepIndex: number) =>
  stepIndex === activeStepIndex
    ? 'active'
    : activeStepIndex < stepIndex
      ? 'incomplete'
      : 'complete';

const WizardLarge = <T extends React.ElementType = 'div'>({
  orientation = 'horizontal',
  activeStepIndex,
  steps,
  as,
  style,
  className,
  htmlAttributes,
  alignment,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, Omit<WizardProps, 'size'>>) => {
  const Component: React.ElementType = as || 'div';

  const wizardClasses = classNames(
    'ndl-wizard',
    'ndl-wizard-large',
    {
      'ndl-horizontal': orientation === 'horizontal',
      'ndl-vertical': orientation === 'vertical',
      [`ndl-wizard-align-${alignment}`]: alignment,
    },
    className,
  );

  const componentStyle = {
    gridTemplateColumns:
      orientation === 'horizontal'
        ? `repeat(${steps.length}, 1fr)`
        : `32px 1fr`,

    gridTemplateRows:
      orientation === 'vertical' && `repeat(${steps.length}, 1fr)`,
  };

  const stepClasses = (index: number, step: Step) => {
    const status = getStatus(activeStepIndex, index);

    return classNames('ndl-wizard-step', {
      'ndl-horizontal': orientation === 'horizontal',
      'ndl-vertical': orientation === 'vertical',
      'ndl-wizard-step-line-right': index < steps.length - 1,
      'ndl-wizard-step-line-left': index > 0,
      'ndl-wizard-step-complete': status === 'complete',
      'ndl-wizard-step-active': status === 'active',
      'ndl-wizard-step-error': step.status === 'error',
      [`ndl-wizard-align-${alignment}`]: alignment,
    });
  };

  return (
    <Component
      className={wizardClasses}
      style={{ ...style, ...componentStyle }}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {/* iterate over the steps to render the text content (the first row of the component) */}
      {steps.map((step, index) => {
        if (typeof step.content === 'string') {
          const wizardStepClasses = classNames('ndl-wizard-step-text', {
            'ndl-vertical': orientation === 'vertical',
            'ndl-horizontal': orientation === 'horizontal',
            [`ndl-wizard-align-${alignment}`]: alignment,
            'ndl-wizard-step-incomplete':
              getStatus(activeStepIndex, index) === 'incomplete',
          });

          return (
            <div key={index} className={wizardStepClasses}>
              <Typography variant="body-large">{step.content}</Typography>
            </div>
          );
        } else {
          return <React.Fragment key={index}>{step.content}</React.Fragment>;
        }
      })}
      {/* iterate over the steps to render the circles (iterates again to render the second row of the component) */}
      {steps.map((step, index) => {
        return (
          <div
            className={stepClasses(index, step)}
            key={index}
            style={{
              gridRow: orientation === 'vertical' ? index + 1 : '',
            }}
            aria-current={
              getStatus(activeStepIndex, index) === 'active'
                ? 'step'
                : undefined
            }
          >
            <Circle
              status={
                step.status === 'error'
                  ? 'error'
                  : getStatus(activeStepIndex, index)
              }
              number={index + 1}
              text={step.content}
              alignment={alignment}
            />
          </div>
        );
      })}
    </Component>
  );
};

export const WizardSmall = <T extends React.ElementType = 'div'>({
  orientation = 'horizontal',
  activeStepIndex,
  steps,
  as,
  className,
  htmlAttributes,
  style,
  alignment = 'middle',
  ref,
  ...restProps
}: PolymorphicCommonProps<T, Omit<WizardProps, 'size'>>) => {
  const Component = as ?? 'div';

  const wizardClasses = classNames(
    'ndl-wizard',
    'ndl-wizard-small',
    {
      'ndl-horizontal': orientation === 'horizontal',
      'ndl-vertical': orientation === 'vertical',
      [`ndl-wizard-align-${alignment}`]: alignment,
    },
    className,
  );

  const stepClasses = (index: number) =>
    classNames('ndl-wizard-step', {
      'ndl-horizontal': orientation === 'horizontal',
      'ndl-vertical': orientation === 'vertical',
      'ndl-wizard-step-active': getStatus(activeStepIndex, index) === 'active',
      'ndl-wizard-step-complete':
        getStatus(activeStepIndex, index) === 'complete',
      [`ndl-wizard-align-${alignment}`]: alignment,
    });

  return (
    <Component
      className={wizardClasses}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-wizard-steps-line" />
      {steps.map((step, index) => {
        return (
          <div
            key={index}
            className={stepClasses(index)}
            aria-current={
              getStatus(activeStepIndex, index) === 'active'
                ? 'step'
                : undefined
            }
          >
            <div className="ndl-wizard-circle"></div>
          </div>
        );
      })}
      <div className="ndl-wizard-steps-line" />
    </Component>
  );
};

const Circle = ({
  status,
  number,
  alignment = 'middle',
}: {
  status: 'active' | 'complete' | 'incomplete' | 'error';
  number: number;
  text: React.ReactNode;
  alignment?: 'top' | 'middle';
}) => {
  const innerCircleClasses = classNames(
    'ndl-wizard-circle-inner',
    `ndl-${status}`,
  );
  const outerCircleClasses = classNames('ndl-wizard-circle', {
    [`ndl-wizard-align-${alignment}`]: alignment,
  });

  return (
    <div className={outerCircleClasses}>
      <svg aria-hidden="true">
        {status === 'active' && (
          <g>
            <circle
              className="ndl-wizard-circle-ring"
              style={{ fill: 'transparent', strokeWidth: 2 }}
              cx="16"
              cy="16"
              r="14"
            />
          </g>
        )}

        <circle className={innerCircleClasses} cx="16" cy="16" r="12" />
        <text
          x="50%"
          y="50%"
          fontSize={14}
          className="ndl-wizard-circle-text"
          textAnchor="middle"
          dominantBaseline="middle"
          dy="0.1em"
        >
          {status === 'error' ? '!' : number}
        </text>
      </svg>
    </div>
  );
};
